<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import { FormInput } from "../../../base-components/Form";
import Button from "../../../base-components/Button";
import { Menu } from "../../../base-components/Headless";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import ModalProductosCategoriasEditar from "./modals/ModalProductosCategoriasEditar.vue";
import { CategoriaProductos } from "@fixeduy/restaurantestypes";
import { useProductosCategoriasStore } from "../../../services/productosCategorias";

const ProductosCategoriaStore = useProductosCategoriasStore();

enum showModalType {
  none,
  Editar
}

const nombreActivo = ref("Ver inactivos");

const verInactivo = ref(false);

onBeforeMount(async () => {
  verInactivo.value = false;
  await ProductosCategoriaStore.getCategoriasFromFirestore();
});

const showModal = ref<showModalType>(showModalType.none);
const categoriaSeleccionado = ref<CategoriaProductos | null>(null);

const showModalEdit = (categoria: CategoriaProductos) => {
  showModal.value = showModalType.Editar;
  categoriaSeleccionado.value = categoria;
};

const cerrarModal = () => {
  showModal.value = showModalType.none;
  categoriaSeleccionado.value = null;
};

const inputBuscar = ref("");

const coincideBusqueda = (categoria: CategoriaProductos) => {
  const buscar = inputBuscar.value.toLocaleLowerCase().trim();
  if (buscar === "") return true;
  const codigo = String(categoria.codigo ?? "").toLocaleLowerCase();
  const nombre = String(categoria.nombre ?? "").toLocaleLowerCase();
  return codigo.includes(buscar) || nombre.includes(buscar);
};

const categoriasVisibles = computed(() =>
  [...ProductosCategoriaStore.categorias]
    .filter(
      categoria =>
        (verInactivo.value || (!verInactivo.value && categoria.activo)) &&
        coincideBusqueda(categoria)
    )
    .sort((a, b) => (a.nombre ?? "").localeCompare(b.nombre ?? ""))
);

const categoriasActivasCount = computed(
  () => ProductosCategoriaStore.categorias.filter(c => c.activo).length
);

const mostrarSinDatos = computed(() => categoriasVisibles.value.length === 0);

const verInactivos = () => {
  verInactivo.value = !verInactivo.value;
  if (!verInactivo.value) nombreActivo.value = "Ver inactivos";
  else nombreActivo.value = "Ver solo activos";
};
</script>

<template>
  <Preview class="mt-5 box" v-slot="{ toggle }">
    <div class="grid grid-cols-3 gap-4">
      <div class="col-span-2 p-5">
        <h2 class="text-base font-medium">Categorias de Productos</h2>
        <FormInput
          class="mt-2"
          type="text"
          placeholder="Buscar por código o nombre..."
          v-model="inputBuscar"
        />
        <Button
          variant="secondary"
          rounded
          class="w-30 mb-2 mr-1 mt-4"
          @click="verInactivos"
        >
          {{ nombreActivo }}
        </Button>
      </div>
    </div>
    <div class="px-5">
      <Preview.Panel>
        <div class="overflow-visible">
          <Table hover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="whitespace-nowrap"> Código</Table.Th>
                <Table.Th class="whitespace-nowrap"> Nombre</Table.Th>
                <Table.Th class="text-center">{{
                  verInactivo ? "Estado" : ""
                }}</Table.Th>
                <Table.Th class="whitespace-nowrap text-right"
                  >Acciones</Table.Th
                >
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr v-if="categoriasVisibles.length === 0">
                <Table.Td colspan="30" class="text-center py-8 text-slate-500">
                  {{
                    categoriasActivasCount === 0 && !verInactivo
                      ? "No tienes ninguna categoría activa."
                      : "No hay categorías para mostrar."
                  }}
                </Table.Td>
              </Table.Tr>
              <Table.Tr
                v-for="categoria in categoriasVisibles"
                :key="categoria.id"
              >
                <Table.Td>{{ categoria.codigo }}</Table.Td>
                <Table.Td>{{ categoria.nombre }}</Table.Td>
                <Table.Td>
                  <div class="flex justify-center">
                    <div
                      v-if="verInactivo"
                      :class="[
                        { 'text-success': categoria.activo },
                        { 'text-danger': !categoria.activo }
                      ]"
                      class="flex items-center"
                    >
                      <Lucide icon="CheckSquare" class="w-4 h-4 mr-2" />
                      <span class="align-middle">{{
                        categoria.activo ? "Activo" : "Inactivo"
                      }}</span>
                    </div>
                  </div>
                </Table.Td>
                <Table.Td>
                  <div class="flex justify-end">
                    <Menu>
                      <Menu.Button :as="Button" class="px-2 !box">
                        <span class="flex items-right justify-center w-5 h-5">
                          <Lucide icon="Plus" class="w-4 h-4" />
                        </span>
                      </Menu.Button>
                      <Menu.Items class="w-40" placement="left-end">
                        <Menu.Item @click="showModalEdit(categoria)">
                          <Lucide icon="Edit2" class="w-4 h-4 mr-2" />
                          Editar
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </div>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      </Preview.Panel>
    </div>
    <ModalProductosCategoriasEditar
      v-if="showModal == showModalType.Editar && categoriaSeleccionado != null"
      :categoria="categoriaSeleccionado!"
      @cerrar="cerrarModal"
    />
  </Preview>
</template>
